import * as XLSX from "xlsx";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function withXlsxExtension(filename: string): string {
    return filename.toLowerCase().endsWith(".xlsx") ? filename : `${filename}.xlsx`;
}

function buildWorkbook(
    headers: readonly string[],
    rows: readonly Record<string, string>[],
    sheetName: string,
): XLSX.WorkBook {
    const aoa: string[][] = [[...headers], ...rows.map((row) => headers.map((h) => row[h] ?? ""))];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(aoa), sheetName);
    return workbook;
}

/**
 * Trigger browser download of an .xlsx file with a single sheet built from
 * headers + rows.
 */
export function downloadXlsx(
    filename: string,
    headers: readonly string[],
    rows: readonly Record<string, string>[],
    sheetName = "Sheet1",
): void {
    XLSX.writeFile(buildWorkbook(headers, rows, sheetName), withXlsxExtension(filename));
}

/**
 * Build an in-memory .xlsx `File` with a single sheet built from headers + rows,
 * e.g. to feed existing mappings through an upload/validate endpoint.
 */
export function buildXlsxFile(
    filename: string,
    headers: readonly string[],
    rows: readonly Record<string, string>[],
    sheetName = "Sheet1",
): File {
    const bytes = XLSX.write(buildWorkbook(headers, rows, sheetName), {
        type: "array",
        bookType: "xlsx",
    }) as ArrayBuffer;
    return new File([bytes], withXlsxExtension(filename), { type: XLSX_MIME });
}

/** Trigger browser download of a `File` already held in memory. */
export function downloadFile(file: File): void {
    const url = window.URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}
